/**
 * Detects GPS tracking gaps in a recorded track — a stretch where tracking was
 * lost, which shows as one long straight-line jump between two consecutive
 * recorded points.
 *
 * A gap requires BOTH a large time delta AND a large straight-line distance.
 * Distance matters: a long *rest* produces a long time delta with ~no distance
 * (the displacement filter correctly stops emitting points), and that's not a
 * tracking failure. On foot, two consecutive fixes can't legitimately be
 * hundreds of metres apart, so a big jump is always lost tracking.
 */

/** Default: ≥ 2 min between consecutive fixes. */
export const DEFAULT_MIN_GAP_MS = 2 * 60 * 1000
/** Default: ≥ 250 m straight-line between consecutive fixes (impossible on foot
 *  in one fix interval → lost tracking, not real travel). */
export const DEFAULT_MIN_GAP_METERS = 250

export interface TrackGap {
  /** Index of the last point before the gap. */
  startIdx: number
  /** Index of the first point after the gap. */
  endIdx: number
  /** Timestamp (ms epoch) at startIdx. */
  fromMs: number
  /** Timestamp (ms epoch) at endIdx. */
  toMs: number
  /** Straight-line distance across the gap, metres. */
  meters: number
}

/** Haversine distance between two [lng, lat] coordinates, in metres. */
export function haversineMeters(a: number[], b: number[]): number {
  const R = 6371000
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const lat1 = toRad(a[1])
  const lat2 = toRad(b[1])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

export interface GapDetectOptions {
  minGapMs?: number
  minGapMeters?: number
}

/**
 * Find significant tracking gaps. `coordTimes` must be parallel to `coords`
 * (epoch ms). Returns gaps in track order.
 */
export function detectTrackGaps(
  coords: number[][],
  coordTimes: number[],
  opts: GapDetectOptions = {}
): TrackGap[] {
  const minMs = opts.minGapMs ?? DEFAULT_MIN_GAP_MS
  const minM = opts.minGapMeters ?? DEFAULT_MIN_GAP_METERS
  const n = Math.min(coords.length, coordTimes.length)
  const gaps: TrackGap[] = []
  for (let i = 1; i < n; i++) {
    const dt = coordTimes[i] - coordTimes[i - 1]
    if (dt < minMs) continue
    const meters = haversineMeters(coords[i - 1], coords[i])
    if (meters < minM) continue
    gaps.push({
      startIdx: i - 1,
      endIdx: i,
      fromMs: coordTimes[i - 1],
      toMs: coordTimes[i],
      meters,
    })
  }
  return gaps
}
